<script setup lang="ts">
  import type { HEX, IconTypes } from '@/types'
  import type { ToastState } from '@/types/ui'

  import { computed } from 'vue'

  import { useUiStore } from '@/stores/ui'
  import { storeToRefs } from 'pinia'

  import ManagerIcon from '@/components/Global/Manager/ManagerIcon.vue'

  const ui = useUiStore()

  const { toastState } = storeToRefs(ui)

  const icon = computed(() => {
    const icons: Record<ToastState['type'], IconTypes> = {
      error: 'error',
      success: 'check',
    }

    return icons[toastState.value.type] ?? null
  })

  const color = computed(() => {
    const colors: Record<ToastState['type'], HEX> = {
      error: '#C74B53',
      success: '#06A616',
    }

    return colors[toastState.value.type] ?? null
  })

  function close() {
    toastState.value.visibility = false
  }
</script>

<template>
  <transition name="fade">
    <div
      v-if="toastState.visibility"
      class="fixed top-[86px] right-[20px] toast pointer-events-auto mx-auto w-96 z-[9999] max-w-full border-l-[10px] bg-white flex items-center justify-between py-4 px-8"
      :style="`border-color: ${color ?? '#000000'};`"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <!-- Icon -->
      <div class="mr-2">
        <ManagerIcon :icon="icon" />
      </div>

      <div class="break-words text-sm">{{ toastState.text }}</div>

      <!-- Close Button -->
      <div>
        <button
          aria-label="close"
          class="close cursor-pointer"
          @click.prevent="close"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 12L12 4M12 12L4 4"
              stroke="#7A7A7A"
              stroke-width="1.2"
              stroke-linecap="square"
            />
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s linear;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .toast {
    box-shadow: 0 2px 9px rgba(0, 0, 0, 0.17);
  }
</style>
